/*
 * Ficha de tienda IGI (/igi/tiendas/:id), pensada para usarse en el celular dentro de la tienda.
 * Pestañas: "Evaluar" (solo con igi_create_evaluation: nueva evaluacion o correccion de una propia),
 * "Historial" (todas las evaluaciones de la tienda con su autor; la vigente marcada) y "Visitas"
 * (historial de visitas de campo, alta/edicion/borrado con igi_create_visit e impresion de acta).
 * Datos: GET /igi/tiendas/:id/evaluaciones/ (redux _get_evaluaciones_tienda: data.datos + data.tienda),
 * GET /igi/criterios/ (_get_all_criterios: data.datos = ejes con criterios) y
 * GET /igi/tiendas/:id/visitas/ (_get_visitas_tienda: data.datos). Las tres pestañas quedan
 * montadas (se ocultan con d-none) para no perder lo escrito al cambiar de pestaña. Si el backend
 * rechaza la tienda (fuera del territorio), se muestra su mensaje.
 * El `status` de los slices de redux queda en 0 tanto antes de pedir como mientras la peticion esta
 * en curso; se usan los flags locales `pedidoTienda`/`pedidoCriterios`/`pedidoVisitas` (mismo patron
 * que pages/admin/igi/mi_zona/index.js, Tarea 7) para distinguir "cargando" del estado inicial y no
 * dejar la pantalla en blanco. El estado de carga/error de criterios se pasa a `Evaluar` por props
 * para que esa pestaña no deje calificar mientras los criterios no han llegado (o fallaron).
 */
import React, {useCallback, useEffect, useState} from "react";
import {useHistory} from "react-router-dom";
import Breadcrumbs from "../../../../components/breadcrumbs";
import Button from "../../../../components/button";
import {ToastContainer} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {useDispatch, useSelector} from "react-redux";
import {Prompt, useParams} from "react-router-dom";
import {
    clear_igi,
    get_all_criterios,
    get_evaluaciones_tienda,
    get_visitas_tienda,
    delete_visita,
    GET_EVALUACIONES_TIENDA_SUCCESS,
    GET_VISITAS_TIENDA_SUCCESS,
    DELETE_VISITA_SUCCESS,
} from "../../../../redux/igi";
import Visitas from "./visitas";
import VisitaFormulario from "./visita-formulario";
import ActaVisita from "./acta-visita";
import {toast} from "react-toastify";
import {NivelBadge, formatoFecha, formatoNota} from "../niveles";
import Evaluar from "./evaluar";
import Historial from "./historial";

/** @module  Pages/Igi/Tienda */

// Un solo <Prompt> para toda la ficha: Evaluar y Visitas quedan ambas montadas (d-none) al cambiar de
// pestaña, y la libreria `history` solo soporta un Prompt activo a la vez — si cada pestaña montara
// el suyo, el segundo pisaria al primero sin avisar. Por eso viven acá arriba, combinando ambos
// flags de cambios sin guardar (conCambios de Evaluar + conCambiosVisita de VisitaFormulario).
const MENSAJE_SALIDA = "Tiene cambios sin guardar. ¿Desea salir sin guardar?";

const FichaTienda = (props) => {
    const {id} = useParams();
    const dispatch = useDispatch();
    const history = useHistory();
    const {token} = useSelector((state) => state.users_module.login.data) || null;
    const evaluaciones = useSelector((state) => state.igi_module._get_evaluaciones_tienda);
    const criterios = useSelector((state) => state.igi_module._get_all_criterios);
    const visitasSlice = useSelector((state) => state.igi_module._get_visitas_tienda);
    const _delete = useSelector((state) => state.igi_module._delete_visita);

    const puedeEvaluar = Object.values(props).some((x) => x && x.codigo === "igi_create_evaluation");
    const puedeVisitar = Object.values(props).some((x) => x && x.codigo === "igi_create_visit");
    const [pestana, setPestana] = useState(puedeEvaluar ? "evaluar" : "historial");
    const [enCorreccion, setEnCorreccion] = useState(null);
    const [conCambios, setConCambios] = useState(false);
    const [conCambiosVisita, setConCambiosVisita] = useState(false);
    const [version, setVersion] = useState(0);
    // status de _get_evaluaciones_tienda / _get_all_criterios queda en 0 antes de pedir y mientras
    // la peticion esta en curso; "pedido" distingue ese "en curso" del estado inicial (Tarea 7).
    const [pedidoTienda, setPedidoTienda] = useState(false);
    const [pedidoCriterios, setPedidoCriterios] = useState(false);
    const [visitaEnEdicion, setVisitaEnEdicion] = useState(undefined); // undefined = no editando; null = nueva; objeto = editar
    const [visitaVersion, setVisitaVersion] = useState(0);
    const [visitaParaImprimir, setVisitaParaImprimir] = useState(null);
    const [pedidoVisitas, setPedidoVisitas] = useState(false);

    const recargar = useCallback(() => {
        setPedidoTienda(true);
        dispatch(get_evaluaciones_tienda(token, id));
    }, [dispatch, token, id]);

    const recargarVisitas = useCallback(() => {
        setPedidoVisitas(true);
        dispatch(get_visitas_tienda(token, id));
    }, [dispatch, token, id]);

    useEffect(() => {
        dispatch(clear_igi(GET_EVALUACIONES_TIENDA_SUCCESS));
        setPedidoCriterios(true);
        dispatch(get_all_criterios(token));
        recargar();
        dispatch(clear_igi(GET_VISITAS_TIENDA_SUCCESS));
        recargarVisitas();
    }, [dispatch, token, recargar, recargarVisitas]);

    const cargandoTienda = pedidoTienda && evaluaciones.status === 0;
    const cargandoCriterios = pedidoCriterios && criterios.status === 0;
    const cargandoVisitas = pedidoVisitas && visitasSlice.status === 0;
    const visitas = visitasSlice.data && visitasSlice.data.datos ? visitasSlice.data.datos : [];
    const errorCriterios = criterios.status !== 0 && criterios.status !== 200
        ? (criterios.message || "No se pudieron cargar los criterios")
        : null;

    const datos = evaluaciones.data && evaluaciones.data.datos ? evaluaciones.data.datos : [];
    const tienda = evaluaciones.data && evaluaciones.data.tienda ? evaluaciones.data.tienda : null;
    const ejes = criterios.data && criterios.data.datos ? criterios.data.datos : [];
    const actual = tienda ? datos.find((e) => e.id === tienda.evaluacion_actual) : null;

    const corregir = (evaluacion) => {
        if (conCambios && !window.confirm("Tiene cambios sin guardar en la evaluación. ¿Descartarlos?")) {
            return;
        }
        setEnCorreccion(evaluacion);
        setPestana("evaluar");
    };

    const alGuardar = useCallback(() => {
        setEnCorreccion(null);
        setVersion((v) => v + 1);
        recargar();
        setPestana("historial");
    }, [recargar]);

    const cancelarCorreccion = useCallback(() => {
        setEnCorreccion(null);
        setVersion((v) => v + 1);
    }, []);

    const nuevaVisita = () => setVisitaEnEdicion(null);
    const editarVisita = (visita) => setVisitaEnEdicion(visita);
    const alGuardarVisita = useCallback(() => {
        setVisitaEnEdicion(undefined);
        setVisitaVersion((v) => v + 1);
        // VisitaFormulario se desmonta (no queda montada con d-none como Evaluar), asi que su propio
        // efecto onCambios(false) no llega a correr antes del desmontaje: se limpia aca tambien.
        setConCambiosVisita(false);
        recargarVisitas();
    }, [recargarVisitas]);
    const cancelarVisita = useCallback(() => {
        setVisitaEnEdicion(undefined);
        setVisitaVersion((v) => v + 1);
        setConCambiosVisita(false);
    }, []);
    const borrarVisita = (visita) => {
        if (!window.confirm("¿Eliminar esta visita? No se puede deshacer.")) {
            return;
        }
        dispatch(delete_visita(token, visita.id));
    };

    useEffect(() => {
        if (_delete.status === 0) {
            return;
        }
        dispatch(clear_igi(DELETE_VISITA_SUCCESS));
        if (_delete.status === 200) {
            toast.success("Visita eliminada");
            recargarVisitas();
        } else {
            toast.error(_delete.message || "No se pudo eliminar la visita");
        }
    }, [_delete, dispatch, recargarVisitas]);

    return (
        <div className="app-content content">
            <Prompt when={conCambios || conCambiosVisita} message={MENSAJE_SALIDA}/>
            <ToastContainer/>
            <div className="content-overlay"/>
            <div className="content-wrapper">
                <div className="content-header row">
                    <div className="content-header-left col-12 mb-2 mt-1 d-flex justify-content-between align-items-start">
                        <Breadcrumbs
                            title={tienda ? `${tienda.codigo} - ${tienda.nombre}` : "Tienda"}
                            items={[{label: "Mi zona", link: "/igi/mi-zona"}]}
                        />
                        <Button
                            type="button"
                            theme="light"
                            text={<><i className="bx bx-arrow-back"/> Volver a Mi zona</>}
                            onChange={() => history.push("/igi/mi-zona")}
                        />
                    </div>
                </div>
                <div className="content-body">
                    {cargandoTienda && (
                        <div className="alert alert-info" role="status" aria-busy="true">
                            <i className="bx bx-loader-alt bx-spin mr-50"/> Cargando tienda…
                        </div>
                    )}
                    {evaluaciones.status !== 0 && evaluaciones.status !== 200 && (
                        <div className="alert alert-danger">{evaluaciones.message || "No se pudo cargar la tienda"}</div>
                    )}
                    {tienda && (
                        <div className="card mb-1">
                            <div className="card-body p-1 d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="text-muted small">{tienda.zona || "Sin zona"}</div>
                                    <div className="small">
                                        Evaluación vigente: {actual ? formatoFecha(actual.fecha) : "Sin evaluación"}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div style={{fontSize: "1.6rem", fontWeight: 700}}>{formatoNota(actual ? actual.igi : null)}</div>
                                    <NivelBadge nivel={actual ? actual.nivel : "Sin evaluación"}/>
                                </div>
                            </div>
                        </div>
                    )}
                    {tienda && (
                        <>
                            <ul className="nav nav-tabs mb-1">
                                {puedeEvaluar && (
                                    <li className="nav-item">
                                        <button
                                            type="button"
                                            className={`nav-link btn btn-link ${pestana === "evaluar" ? "active" : ""}`}
                                            onClick={() => setPestana("evaluar")}
                                        >
                                            {enCorreccion ? "Corregir" : "Evaluar"}
                                        </button>
                                    </li>
                                )}
                                <li className="nav-item">
                                    <button
                                        type="button"
                                        className={`nav-link btn btn-link ${pestana === "historial" ? "active" : ""}`}
                                        onClick={() => setPestana("historial")}
                                    >
                                        Historial ({datos.length})
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        type="button"
                                        className={`nav-link btn btn-link ${pestana === "visitas" ? "active" : ""}`}
                                        onClick={() => setPestana("visitas")}
                                    >
                                        Visitas ({visitas.length})
                                    </button>
                                </li>
                            </ul>
                            {puedeEvaluar && (
                                <div className={pestana === "evaluar" ? "" : "d-none"}>
                                    <Evaluar
                                        key={`${enCorreccion ? enCorreccion.id : "nueva"}-${version}`}
                                        token={token}
                                        tiendaId={id}
                                        ejes={ejes}
                                        criteriosCargando={cargandoCriterios}
                                        criteriosError={errorCriterios}
                                        evaluacion={enCorreccion}
                                        anterior={actual}
                                        onGuardado={alGuardar}
                                        onCancelarCorreccion={cancelarCorreccion}
                                        onCambios={setConCambios}
                                    />
                                </div>
                            )}
                            <div className={pestana === "historial" ? "" : "d-none"}>
                                <Historial
                                    evaluaciones={datos}
                                    actualId={tienda.evaluacion_actual}
                                    ejes={ejes}
                                    onCorregir={corregir}
                                />
                            </div>
                            <div className={pestana === "visitas" ? "" : "d-none"}>
                                {cargandoVisitas && (
                                    <div className="alert alert-info" role="status" aria-busy="true">
                                        <i className="bx bx-loader-alt bx-spin mr-50"/> Cargando visitas…
                                    </div>
                                )}
                                {visitaEnEdicion !== undefined ? (
                                    <VisitaFormulario
                                        key={`${visitaEnEdicion ? visitaEnEdicion.id : "nueva"}-${visitaVersion}`}
                                        token={token}
                                        tiendaId={id}
                                        visita={visitaEnEdicion}
                                        onGuardado={alGuardarVisita}
                                        onCancelar={cancelarVisita}
                                        onCambios={setConCambiosVisita}
                                    />
                                ) : (
                                    <Visitas
                                        visitas={visitas}
                                        puedeVisitar={puedeVisitar}
                                        onNueva={nuevaVisita}
                                        onEditar={editarVisita}
                                        onBorrar={borrarVisita}
                                        onImprimir={setVisitaParaImprimir}
                                    />
                                )}
                            </div>
                            <ActaVisita
                                visita={visitaParaImprimir}
                                tienda={tienda}
                                evaluacionVigente={actual}
                                onClose={() => setVisitaParaImprimir(null)}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FichaTienda;
